import { useEffect, useMemo, useRef } from 'react';
import { HubConnection } from '@microsoft/signalr';
import { SignalRHubUrls } from '../../types/constants/apiUrls/signalr/hubUrls';
import { useSignalRHub } from './useSignalRHub';
import { StudioBatchEventTypes, StudioBatchItemUpdatedPayload } from './types';

const BOARD_GROUP_KEY = 'studio-batch-tracking:global';

export type UseStudioBatchTrackingHubOptions = {
    enabled?: boolean;
    onItemUpdated?: (payload: StudioBatchItemUpdatedPayload) => void;
    /** Debounced list/metrics refetch — always invoked on metricsUpdated, and on itemUpdated fallback. */
    onRefresh?: () => void;
    /** Fired when the connection came back after a drop: REST refetch to catch missed events. */
    onReconnected?: () => void;
};

function pickString(p: Record<string, unknown>, camel: string, pascal: string): string {
    const value = p[camel] ?? p[pascal];
    return typeof value === 'string' ? value : '';
}

function normalizeStudioBatchPayload(payload: unknown): StudioBatchItemUpdatedPayload | null {
    let parsed: unknown = payload;
    if (typeof payload === 'string') {
        try {
            parsed = JSON.parse(payload);
        } catch {
            return null;
        }
    }

    if (!parsed || typeof parsed !== 'object') {
        return null;
    }

    const p = parsed as Record<string, unknown>;
    const barcode = pickString(p, 'barcode', 'Barcode');
    if (!barcode) {
        return null;
    }

    const aggregateStatusRaw = p.aggregateStatus ?? p.AggregateStatus;
    const updatedAtRaw = p.updatedAtUtc ?? p.UpdatedAtUtc;

    return {
        barcode,
        aggregateStatus:
            typeof aggregateStatusRaw === 'string' ? aggregateStatusRaw : aggregateStatusRaw == null ? null : String(aggregateStatusRaw),
        createdBy: pickString(p, 'createdBy', 'CreatedBy') || null,
        assignee: pickString(p, 'assignee', 'Assignee') || null,
        updatedAtUtc:
            typeof updatedAtRaw === 'string'
                ? updatedAtRaw
                : updatedAtRaw instanceof Date
                  ? updatedAtRaw.toISOString()
                  : String(updatedAtRaw ?? ''),
    };
}

export function useStudioBatchTrackingHub(options: UseStudioBatchTrackingHubOptions) {
    const { enabled = true, onItemUpdated, onRefresh, onReconnected } = options;

    const itemRef = useRef(onItemUpdated);
    const refreshRef = useRef(onRefresh);
    const reconnectedRef = useRef(onReconnected);
    itemRef.current = onItemUpdated;
    refreshRef.current = onRefresh;
    reconnectedRef.current = onReconnected;

    const triggerRefresh = () => {
        refreshRef.current?.();
    };

    const eventHandlers = useMemo(
        () => ({
            [StudioBatchEventTypes.ItemUpdated]: (payload: unknown) => {
                const normalized = normalizeStudioBatchPayload(payload);
                if (normalized) {
                    itemRef.current?.(normalized);
                } else {
                    triggerRefresh();
                }
            },
            [StudioBatchEventTypes.MetricsUpdated]: () => {
                triggerRefresh();
            },
        }),
        [],
    );

    const hub = useSignalRHub({
        hubUrl: SignalRHubUrls.StudioBatchTracking,
        protocol: 'json',
        groupKey: BOARD_GROUP_KEY,
        enabled,
        join: async (connection: HubConnection) => {
            await connection.invoke('JoinBoard');
        },
        leave: async (connection: HubConnection) => {
            await connection.invoke('LeaveBoard');
        },
        eventHandlers,
    });

    const wasConnectedRef = useRef(false);
    useEffect(() => {
        if (hub.connectionState === 'connected') {
            if (wasConnectedRef.current) {
                reconnectedRef.current?.();
            }
            wasConnectedRef.current = true;
        }
    }, [hub.connectionState]);

    return hub;
}
